import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useRole, NAV_SECTIONS, EMPLOYEE_ALLOWED_PATHS } from '@/contexts/RoleContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { incomingInvoicesAPI } from '@/lib/api';
import {
  LayoutDashboard, Package, LogOut,
  User, Store, Users, CreditCard,
  ScrollText, Database, BookOpen, ChevronDown, PiggyBank, Zap, Settings, Lock,
  AlertCircle, ShoppingCart, Truck, PackagePlus, BarChart2, Calculator,
  Receipt, History, TrendingUp, Wallet, Menu, X,
} from 'lucide-react';
import { TIER_UPGRADE_MSG } from '@/lib/tierConfig';
import OfflineBanner from '@/components/OfflineBanner';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { Maximize } from 'lucide-react';
import { enterFullscreenWithEscapeLock } from '@/lib/fullscreenUtils';

const DataAsBanner = () => null;

// Mobile bottom nav — 3 primary tabs + "More"
const MOBILE_NAV_TABS = [
  { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { path: '/pos',       label: 'Billing',  icon: ShoppingCart    },
  { path: '/purchases', label: 'Purchase', icon: PackagePlus     },
];

// Individual nav item icons — direct links and appendix sub-items
const ITEM_ICONS = {
  '/savings':            PiggyBank,
  '/smart-actions':      Zap,
  '/dashboard':          LayoutDashboard,
  '/stock':              Package,
  '/customer-insights':  Users,
  '/credit':             CreditCard,
  '/credit-customers':   Wallet,
  '/stock-master':       Database,
  '/appendix':           BookOpen,
  '/pos':                ShoppingCart,
  '/sales-history':      ScrollText,
  '/purchases':          PackagePlus,
  '/purchases/history':  History,
  '/reports':            TrendingUp,
  '/accounting':         Calculator,
  'dashboard':           LayoutDashboard,
  'billing':             Receipt,
  'purchase':            Truck,
  'reports':             BarChart2,
};

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, storeProfile, logout, isImpersonation } = useAuth();
  const { hasPermission, isPageLocked } = useRole();
  const { t } = useLanguage();
  const navRef = useRef(null);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Pending incoming invoice count — shown as badge on Purchase nav item
  const canSeeBadge = user?.role === 'owner' || user?.role === 'manager';
  const { data: incomingCountData } = useQuery({
    queryKey: ['incoming-invoice-count'],
    queryFn: () => incomingInvoicesAPI.getCount(),
    select: r => r.data?.count ?? 0,
    staleTime: 60_000,
    enabled: canSeeBadge,
  });
  const incomingCount = incomingCountData ?? 0;

  // ── Build visible sections for this user ────────────────────────────────
  const CASHIER_PATHS = new Set(['/pos', '/stock', '/purchases', '/appendix']);
  const visibleSections = NAV_SECTIONS.map(section => ({
    ...section,
    visibleItems: section.items.filter(item =>
      hasPermission(item.permission) &&
      (!item.hideFor || !item.hideFor.includes(user?.role)) &&
      (user?.role !== 'cashier' || CASHIER_PATHS.has(item.path))
    ),
  })).filter(section => section.visibleItems.length > 0);

  // ── Track open sections (multiple can be open simultaneously) ────────────
  const getActiveSectionId = (pathname) =>
    NAV_SECTIONS.find(s => s.items.some(i => pathname === i.path))?.id ?? null;

  const [openSections, setOpenSections] = useState(new Set());

  // Auto-open the section containing the current page, and auto-collapse
  // any other section (including one left behind on navigation).
  React.useEffect(() => {
    const activeId = getActiveSectionId(location.pathname);
    setOpenSections(activeId ? new Set([activeId]) : new Set());
  }, [location.pathname]);

  // Keep a ref to latest nav state so keyboard handlers don't go stale
  const navStateRef = useRef({ visibleSections, pathname: location.pathname });
  useEffect(() => {
    navStateRef.current = { visibleSections, pathname: location.pathname };
  });

  // Global keyboard shortcuts:
  //   Ctrl+B   → POS (billing)
  //   Ctrl+M   → Appendix (Stock Master tab)
  //   Alt+↑/↓  → previous/next sidebar page
  // Registered on the capture phase so Alt+↑/↓ reaches sidebar navigation
  // before a focused search box's own (non-Alt) ArrowUp/ArrowDown handler
  // can intercept it — e.g. the POS product search or purchase-page search.
  // Native <select> reserves Alt+↑/↓ for opening its dropdown, so that
  // element (and contentEditable) are left alone.
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        navigate('/pos');
        return;
      }

      if (e.ctrlKey && e.key === 'm') {
        e.preventDefault();
        navigate('/appendix');
        return;
      }

      if (e.altKey && !e.ctrlKey && !e.shiftKey &&
          (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        const tag = (e.target instanceof Element) ? e.target.tagName : '';
        if (tag === 'SELECT' || e.target.isContentEditable) return;
        e.preventDefault();
        e.stopPropagation();
        const { visibleSections: sections, pathname } = navStateRef.current;
        const allPaths = sections.flatMap(s => s.visibleItems.map(i => i.path));
        if (allPaths.length === 0) return;
        const currentIdx = allPaths.indexOf(pathname);
        if (e.key === 'ArrowUp') {
          navigate(allPaths[currentIdx <= 0 ? allPaths.length - 1 : currentIdx - 1]);
        } else {
          navigate(allPaths[currentIdx >= allPaths.length - 1 ? 0 : currentIdx + 1]);
        }
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [navigate]);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(sectionId) ? next.delete(sectionId) : next.add(sectionId);
      return next;
    });
  };


  // ── Build flat navNodes list for keyboard navigation ─────────────────────
  // Recomputed on every render (depends on visibleSections + openSections)
  const navNodes = [];
  visibleSections.forEach(section => {
    if (!section.sectionLabel) {
      const item = section.visibleItems[0];
      if (item) navNodes.push({ type: 'direct', sectionId: section.id, path: item.path });
    } else {
      navNodes.push({ type: 'section', sectionId: section.id });
      if (openSections.has(section.id)) {
        section.visibleItems.forEach(item =>
          navNodes.push({ type: 'item', sectionId: section.id, path: item.path })
        );
      }
    }
  });

  const focusNavNode = (idx) => {
    if (idx < 0 || idx >= navNodes.length) return;
    const el = navRef.current?.querySelector(`[data-nav="${idx}"]`);
    el?.focus();
  };

  const getCurrentNavIdx = () => {
    for (let i = 0; i < navNodes.length; i++) {
      const el = navRef.current?.querySelector(`[data-nav="${i}"]`);
      if (el && (el === document.activeElement || el.contains(document.activeElement))) return i;
    }
    return -1;
  };

  const handleNavKeyDown = (e) => {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    e.preventDefault();

    const idx = getCurrentNavIdx();
    if (idx === -1) { focusNavNode(0); return; }

    const node = navNodes[idx];

    if (e.key === 'ArrowDown') {
      focusNavNode(idx + 1);
    } else if (e.key === 'ArrowUp') {
      focusNavNode(idx - 1);
    } else if (e.key === 'ArrowRight') {
      if (node?.type === 'section') {
        if (!openSections.has(node.sectionId)) {
          // Open section, then focus first child once rendered
          toggleSection(node.sectionId);
          requestAnimationFrame(() => {
            // After re-render the children are inserted right after this node
            focusNavNode(idx + 1);
          });
        } else {
          // Already open — move focus to first child
          focusNavNode(idx + 1);
        }
      }
    } else if (e.key === 'ArrowLeft') {
      if (node?.type === 'item') {
        // Find the parent section header index
        let headerIdx = -1;
        for (let i = idx - 1; i >= 0; i--) {
          if (navNodes[i].type === 'section' && navNodes[i].sectionId === node.sectionId) {
            headerIdx = i; break;
          }
        }
        toggleSection(node.sectionId);
        if (headerIdx >= 0) {
          requestAnimationFrame(() => focusNavNode(headerIdx));
        }
      } else if (node?.type === 'section' && openSections.has(node.sectionId)) {
        toggleSection(node.sectionId);
      }
    }
  };

  const [moreOpen, setMoreOpen] = useState(false);

  const displayRole = 'Customer';
  const currentStoreName = storeProfile?.shop_name || user?.shop_name || 'My Store';

  // All nav items flattened — used by the mobile More drawer
  const allNavItems = NAV_SECTIONS.flatMap(section =>
    section.items.filter(item =>
      hasPermission(item.permission) &&
      (!item.hideFor || !item.hideFor.includes(user?.role))
    )
  );
  const moreItems = allNavItems.filter(item =>
    !MOBILE_NAV_TABS.some(t => t.path === item.path)
  );

  return (
    <div className={`flex flex-col md:flex-row h-screen bg-background${isImpersonation ? ' pt-10' : ''}`}>

      {/* Re-enter fullscreen + re-lock Escape — always available since hold-Escape or
          F11 can silently drop the app out of the guarded fullscreen (see fullscreenUtils.js).
          Desktop-only: Escape/F11/fullscreen is a browser-window concept, not a mobile one. */}
      <button
        type="button"
        onClick={() => enterFullscreenWithEscapeLock()}
        title="Re-enter fullscreen"
        aria-label="Re-enter fullscreen"
        className="hidden md:flex fixed top-3 right-3 z-50 h-8 w-8 items-center justify-center rounded-full bg-foreground/10 text-foreground/50 opacity-60 hover:opacity-100 hover:bg-foreground/20 hover:text-foreground/80 transition-all"
      >
        <Maximize className="h-4 w-4" />
      </button>

      {/* ── Mobile top bar ── */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b bg-card shrink-0">
        <span className="font-bold text-primary text-sm">{t('app.name')}</span>
        <span className="text-xs text-muted-foreground truncate max-w-[180px]">{currentStoreName}</span>
      </div>

      {/* ── Sidebar (desktop only) ── */}
      <aside className="hidden md:flex w-64 bg-card border-r border-border flex-col">

        {/* Store header */}
        <div className="p-5 border-b border-border">
          <h1 className="text-xl font-bold text-primary">{t('app.name')}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-medium truncate flex items-center gap-1.5">
            <Store className="h-3.5 w-3.5 flex-shrink-0" />
            {currentStoreName}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              <span className="truncate max-w-[90px]">{user?.owner_name || user?.shop_name}</span>
            </div>
            <span className="text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap bg-emerald-100 text-emerald-700">
              {displayRole}
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav ref={navRef} onKeyDown={handleNavKeyDown} className="flex-1 p-3 overflow-y-auto space-y-0.5">
          {visibleSections.map(section => {

            /* ── Dashboard: direct link, no collapsible header ── */
            if (!section.sectionLabel) {
              const item = section.visibleItems[0];
              if (!item) return null;
              const Icon = ITEM_ICONS[item.path] || Package;
              const isActive = location.pathname === item.path;
              const nodeIdx = navNodes.findIndex(n => n.type === 'direct' && n.path === item.path);
              const locked = isPageLocked(item.path);

              if (locked) {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}-locked`}
                    data-nav={nodeIdx}
                    className="block rounded-lg"
                  >
                    <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-colors text-muted-foreground/50 hover:bg-muted/30 hover:text-muted-foreground/70">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{item.label}</span>
                      <div className="ml-auto flex items-center gap-1 bg-muted/60 px-1.5 py-0.5 rounded">
                        <Lock className="h-2.5 w-2.5" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide">Locked</span>
                      </div>
                    </div>
                  </Link>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  data-nav={nodeIdx}
                  className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                >
                  <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}>
                    <Icon className="h-4 w-4" />
                    <span className="text-sm">{item.label}</span>
                  </div>
                </Link>
              );
            }

            /* ── Collapsible section (Dashboard, Reports) ── */
            const SectionIcon = ITEM_ICONS[section.id] || Package;
            const isOpen = openSections.has(section.id);
            const sectionHasActive = section.visibleItems.some(i => location.pathname === i.path);
            const sectionNodeIdx = navNodes.findIndex(n => n.type === 'section' && n.sectionId === section.id);
            const allSectionLocked = section.visibleItems.every(i => isPageLocked(i.path));

            return (
              <div key={section.id}>
                {/* Section header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  data-nav={sectionNodeIdx}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg font-medium transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                    allSectionLocked
                      ? 'text-muted-foreground/50 hover:bg-muted/30 hover:text-muted-foreground/70'
                      : sectionHasActive && !isOpen
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <SectionIcon className="h-4 w-4" />
                    <span className="text-sm">{t('nav.' + section.id) !== 'nav.' + section.id ? t('nav.' + section.id) : section.sectionLabel}</span>
                  </div>
                  {allSectionLocked ? (
                    <div className="flex items-center gap-1 bg-muted/60 px-1.5 py-0.5 rounded">
                      <Lock className="h-2.5 w-2.5" />
                      <span className="text-[10px] font-semibold uppercase tracking-wide">Locked</span>
                    </div>
                  ) : (
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-100 ${isOpen ? 'rotate-180' : ''}`} />
                  )}
                </button>

                {/* Sub-items */}
                <div className={`overflow-hidden transition-all duration-100 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="mt-0.5 ml-2 pl-3 border-l border-border space-y-0.5">
                    {section.visibleItems.map(item => {
                      const Icon = ITEM_ICONS[item.path] || Package;
                      const isActive = location.pathname === item.path;
                      const itemLocked = isPageLocked(item.path);
                      const itemNodeIdx = navNodes.findIndex(n => n.type === 'item' && n.path === item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.path.slice(1)}${itemLocked ? '-locked' : ''}`}
                          data-nav={itemNodeIdx}
                          className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                        >
                          <div className={`flex items-center gap-3 px-3 py-2 rounded-lg font-medium transition-colors ${
                            itemLocked
                              ? 'text-muted-foreground/50 hover:bg-muted/30 hover:text-muted-foreground/70'
                              : isActive
                                ? 'bg-primary text-primary-foreground'
                                : 'text-foreground hover:bg-muted'
                          }`}>
                            <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="text-sm">{item.label}</span>
                            {item.path === '/purchases' && incomingCount > 0 && (
                              <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                                {incomingCount > 99 ? '99+' : incomingCount}
                              </span>
                            )}
                            {itemLocked && <Lock className="h-2.5 w-2.5 ml-auto" />}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-border space-y-1.5">
<Link to="/settings" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors w-full">
            <Settings className="h-4 w-4 shrink-0" />
            <span>Settings</span>
          </Link>
          <Button variant="outline" size="sm"
            className="w-full justify-start gap-3 text-destructive hover:text-destructive"
            onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4" />
            <span className="text-sm">{t('common.logout')}</span>
          </Button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 overflow-auto flex flex-col min-h-0">
        <ImpersonationBanner />
        <DataAsBanner storeProfile={storeProfile} />
        <OfflineBanner />
        <div className="flex-1 flex flex-col min-h-0">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className="flex-1 overflow-auto"
            >
              <Outlet key={user?.store_id} />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── Mobile bottom nav ── */}
        <nav className="md:hidden shrink-0 border-t bg-card flex items-stretch safe-area-pb">
          {MOBILE_NAV_TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            const to = tab.path === '/purchases' && location.search.includes('view=history')
              ? '/purchases?view=history'
              : tab.path;
            return (
              <Link
                key={tab.path}
                to={to}
                className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="nav-pill"
                    className="absolute inset-x-1 top-0 h-0.5 rounded-full bg-primary"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                  />
                )}
                <motion.div
                  animate={{ scale: isActive ? 1.12 : 1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 28 }}
                  className="relative"
                >
                  <Icon className="h-5 w-5" />
                  {tab.path === '/purchases' && incomingCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-0.5">
                      {incomingCount > 9 ? '9+' : incomingCount}
                    </span>
                  )}
                </motion.div>
                {tab.label}
              </Link>
            );
          })}

          {/* More tab */}
          <button
            onClick={() => setMoreOpen(v => !v)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors ${
              moreOpen ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Menu className="h-5 w-5" />
            More
          </button>
        </nav>
      </main>

      {/* ── Mobile More drawer ── */}
      {moreOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex flex-col justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />

          {/* Drawer panel */}
          <div className="relative bg-card rounded-t-2xl shadow-xl max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <span className="text-sm font-semibold">More</span>
              <button onClick={() => setMoreOpen(false)}>
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 py-2">
              {moreItems.map(item => {
                const Icon = ITEM_ICONS[item.path] || Package;
                const isActive = location.pathname === item.path;
                const locked = isPageLocked(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMoreOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      locked
                        ? 'text-muted-foreground/50'
                        : isActive
                          ? 'text-primary bg-primary/5'
                          : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {locked && <Lock className="h-3 w-3" />}
                  </Link>
                );
              })}

              {/* Divider + Settings + Logout */}
              <div className="border-t my-2" />
              <Link
                to="/settings"
                onClick={() => setMoreOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:bg-muted"
              >
                <Settings className="h-4 w-4" /> Settings
              </Link>
              <button
                onClick={() => { setMoreOpen(false); logout(); navigate('/login'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-destructive hover:bg-muted"
              >
                <LogOut className="h-4 w-4" /> {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Layout;
